import { jsx as _jsx } from "react/jsx-runtime";
import mapboxgl from 'mapbox-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { render } from 'react-dom';
import { isDestinationMarker, isTownMarker, isTripMarker } from '../data/helpers';
import Marker from '../modules/Marker';
const PATH_COLOR_BG = 'white';
const PATH_COLOR_BASE = '#e3e3de';
const PATH_COLOR_ACTIVE = '#80724a';
const getRouteSourceId = (path) => `${path}_source`;
const getRouteLayerId = (path, style) => `${path}_layer_${style}`;
const isForegroundLayer = (layerId) => layerId.endsWith('_layer_fg');
const isTripMarkerRoute = (route) => {
    return route.meta ? isTripMarker(route.meta.marker) : false;
};
const renderMarker = ({ container, props }, { active = false, visible = true }) => {
    const mergedProps = { ...props, active, visible };
    render(_jsx(Marker, { ...mergedProps }), container);
};
export default function useMapMarkers({ map, routes, shouldRender, fadeDuration, setRoute, includeMajorCities = false, }) {
    const refRenders = useRef([]);
    const refPathMarkers = useRef([]);
    const refSourceIds = useRef(new Set());
    const refLayerIds = useRef(new Set());
    // const refBounds = useRef<mapboxgl.LngLatBounds>(new mapboxgl.LngLatBounds())
    const [bounds, setBounds] = useState(new mapboxgl.LngLatBounds());
    const update = useCallback((path) => {
        // Update markers
        for (const renderProps of refRenders.current) {
            const active = renderProps.props.path === path;
            renderProps.container.style.zIndex = active ? '1' : '0';
            renderMarker(renderProps, { active });
        }
        // Update route paths
        const inst = map.current;
        if (inst && path) {
            const targetLayer = getRouteLayerId(path, 'fg');
            const targetLayerExists = refLayerIds.current.has(targetLayer);
            refLayerIds.current.forEach((layerId) => {
                if (!isForegroundLayer(layerId))
                    return;
                const active = layerId === targetLayer;
                if (active) {
                    inst.setPaintProperty(layerId, 'line-color', PATH_COLOR_ACTIVE);
                }
                else {
                    inst.setPaintProperty(layerId, 'line-color', PATH_COLOR_BASE);
                    if (targetLayerExists) {
                        inst.moveLayer(layerId, targetLayer);
                    }
                }
            });
        }
    }, [refRenders]);
    useEffect(() => {
        const inst = map.current;
        if (inst && shouldRender) {
            // Helpers for drive time paths
            const addPathSource = ({ path, meta }) => {
                const sourceId = getRouteSourceId(path);
                inst.addSource(sourceId, {
                    type: 'geojson',
                    data: meta.marker.route.path,
                });
                refSourceIds.current.add(sourceId);
            };
            const addPathLayer = ({ path }, style) => {
                const sourceId = getRouteSourceId(path);
                const layerId = getRouteLayerId(path, style);
                inst.addLayer({
                    id: layerId,
                    type: 'line',
                    source: sourceId,
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                    },
                    paint: {
                        'line-color': style === 'bg' ? PATH_COLOR_BG : PATH_COLOR_BASE,
                        'line-width': style === 'bg' ? 10 : 5,
                    },
                    minzoom: 0,
                    maxzoom: 24,
                });
                refLayerIds.current.add(layerId);
            };
            // Reset bounds and render array
            refRenders.current = [];
            const bounds = new mapboxgl.LngLatBounds();
            // Find all routes with TripMarker markers
            const driveTimeRoutes = routes.reduce((acc, route) => {
                if (isTripMarkerRoute(route))
                    return [...acc, route];
                else
                    return acc;
            }, []);
            // Find all unique TripMarker endpoints
            const endpoints = driveTimeRoutes.reduce((acc, route) => {
                // Test to see if an endpoint has already been found
                const hasEndpoint = (endpoint) => acc.some((point) => {
                    return point[0] === endpoint[0] && point[1] === endpoint[1];
                });
                // Add start and finish endpoints if not already added
                const { start, end } = route.meta.marker.route;
                for (const endpoint of [start, end]) {
                    if (!hasEndpoint(endpoint))
                        acc.push(endpoint);
                }
                return acc;
            }, []);
            // Create sources and background GeoJSON features for drive time routes
            for (const route of driveTimeRoutes) {
                addPathSource(route);
                addPathLayer(route, 'bg');
            }
            // Create foreground GeoJSON features for drive time routes
            for (const route of driveTimeRoutes) {
                addPathLayer(route, 'fg');
            }
            // Create drive time route marker endpoints
            if (endpoints.length) {
                const pinElem = new Image(32, 32);
                pinElem.src = window.mainSiteData.styleUrl + '/assets/img/path-marker.png';
                const getElement = () => pinElem.cloneNode();
                for (const endpoint of endpoints) {
                    const marker = new mapboxgl
                        .Marker({ anchor: 'center', element: getElement() })
                        .setLngLat(endpoint)
                        .addTo(inst);
                    refPathMarkers.current.push(marker);
                    bounds.extend(marker.getLngLat());
                }
            }
            // Create markers
            for (const route of routes) {
                const { marker, variant } = route.meta || {};
                if (marker && variant && marker.lnglat) {
                    if (marker.lnglat[0] !== 0 && marker.lnglat[1] !== 0) {
                        const anchor = isTripMarker(marker) ? 'center' : 'bottom';
                        const type = isTripMarker(marker) ? 'circle' : 'pin';
                        const container = document.createElement('div');
                        const markerObj = new mapboxgl.Marker(container, { anchor })
                            .setLngLat(marker.lnglat)
                            .addTo(inst);
                        const props = {
                            path: route.path,
                            label: isTownMarker(marker) && !isDestinationMarker(marker) ? marker.title : '',
                            icon: isDestinationMarker(marker) ? marker.icon : isTownMarker(marker) ? marker.icon : 'car',
                            type,
                            variant,
                            fadeDuration,
                            onClick: () => setRoute(route),
                            onRemove: () => markerObj.remove(),
                        };
                        // Add to markers array and expand bounds
                        refRenders.current.push({ container, props });
                        bounds.extend(markerObj.getLngLat());
                    }
                }
            }
            // Inject Brisbane and GC bounds into bounds object so they are visible at initial zoom
            if (includeMajorCities) {
                // bounds.extend([153.0260, -27.4705]) // Brisbane
                bounds.extend([152.7608, -27.6146]); // Ipswich
                bounds.extend([153.4000, -28.0167]); // Gold Coast
            }
            // Render all markers then fit map bounds to them
            update();
            setBounds(bounds);
            // Clean-up
            return () => {
                // Re-call the render function for each marker with the `visible` prop
                // explicitly set to false; this causes AnimatePresence to animate
                // the marker out and consequently call the `onRemove` function
                for (const renderProps of refRenders.current) {
                    renderMarker(renderProps, { visible: false });
                }
                // Remove all sources and layers
                refLayerIds.current.forEach((id) => inst.removeLayer(id));
                refSourceIds.current.forEach((id) => inst.removeSource(id));
                refPathMarkers.current.forEach((marker) => marker.remove());
                // Clean up refs
                refLayerIds.current.clear();
                refSourceIds.current.clear();
                refPathMarkers.current = [];
            };
        }
    }, [map, routes, setRoute, shouldRender, includeMajorCities, update]);
    return { bounds, update };
}
